import styled from 'styled-components';
import { PropertySearchResult } from '../../dtos';
import CardHeader from './CardHeader';
import CardDescription from './CardDescription';
import CardPaymentPlan from './CardPaymentPlan';
import CallToAction from '../../../../new-components/cards/components/CallToAction';
import { isPropertySearchResult } from '../../helpers';
import { EntityTypes } from '../../../../new-lib/constants/common';
import CardAmenities from './CardAmenities';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import { commifyNumber } from '../../../../helpers/common';
import { useTranslation } from 'next-i18next';

interface CardDetailsProps {
  entity: PropertySearchResult;
}
const CardDetails = ({ entity }: CardDetailsProps) => {
  const isMobile = useSelector((state: any) => state.app.isMobile);
  const { t } = useTranslation(['search', 'common']);
  const { locale } = useRouter();
  const description =
    (locale == 'en'
      ? entity.search_subtitle?.en
      : entity.search_subtitle?.ar) || '';

  const getInstallment = () => {
    if (!isPropertySearchResult(entity)) return null;

    const offer = entity.offers?.[0];
    if (offer?.equal_installments?.value) {
      return `${offer.installment_type?.name === 'Back Loaded' ? t('starting_from', { ns: 'common' }) : ''} ${commifyNumber(offer.equal_installments.value)}`;
    }

    if (entity.min_installments && entity.min_installments > 0) {
      return `${entity.installment_type === 'Back Loaded' ? t('starting_from', { ns: 'common' }) : ''} ${commifyNumber(entity.min_installments)}`;
    }

    return null;
  };

  const getYears = () => {
    if (isPropertySearchResult(entity) && entity.offers?.[0]) {
      return entity.offers[0]?.years;
    }
    return entity?.max_installment_years;
  };

  return (
    <Container>
      <CardHeader
        name={entity.name}
        area={entity.area.name}
        developerLogo={entity.developer?.logo_path}
        propertyType={entity?.property_type.name}
        compound={entity?.compound.name}
        description={description}
      />
      {!isMobile && <CardDescription description={description} />}

      <CardAmenities
        bedrooms={entity.number_of_bathrooms}
        bathrooms={entity.number_of_bathrooms}
        min_area={entity.min_unit_area}
      />

      <div className="card-footer">
        <CardPaymentPlan
          paymentPlan={{
            downPayment: null,
            price: entity?.min_price,
            years: getYears(),
            currency: entity?.currency,
            installment: getInstallment()
          }}
        />
        <CallToAction
          entity={entity}
          entityType={
            isPropertySearchResult(entity)
              ? EntityTypes.PROPERTY
              : EntityTypes.COMPOUND
          }
          clickEventData={{}}
        />
      </div>
    </Container>
  );
};

const Container = styled.div`
  max-width: 100%;
  width: calc(100% - 320px);
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 0;
  flex: 1 0 0;
  height: 100%;

  & > *:not(.card-footer) {
    flex-grow: 1;
  }

  .card-footer {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  @media (max-width: ${props => props.theme.breakPoints.mobile.max}) {
    padding: 0 16px;
    gap: 16px;
    width: 100%;
    .card-footer {
      flex-direction: column;
      align-items: start;
      justify-content: start;
      gap: 16px;
    }
  }
`;

export default CardDetails;
